import React from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import * as Ark from '@dateam/ark';
import { useNavigation, withRouter } from 'shared-utils';
import { Button, Card, ConfirmDialog, Form, Loader } from 'shared-ui';
import Page from 'components/Page';
import { SidePanelList, SidePanelListItem, SidePanelListItemCol } from 'components/SidePanelList';
import { VarietalTypeNavigation } from 'screens/routes';
import { TrashIcon } from 'components/Icons';
import { errorProvider } from 'components/errorHandler';
import { FooterDateInfo } from 'components/FooterDateInfo';
import { useVarietalType, useDeleteVarietalType, useUpdateVarietalType } from 'data/varietal';
import config from 'config';
import VarietalTypeDetailsBreadcrumb from './VarietalTypeDetailsBreadcrumb';
import styles from './VarietalTypeDetailsScreen.module.scss';

const VarietalTypeDetailsScreen: React.FC = () => {
    const { t } = useTranslation();
    const {
        push: navigate,
        query: { varietalTypeId: varietalTypeIdParam },
        replace: replaceNavigation
    } = useNavigation();
    const varietalTypeId = React.useMemo(() => parseInt(varietalTypeIdParam, 10), [varietalTypeIdParam]);
    const { data: type, isLoading, isFetched: hasLoaded } = useVarietalType(varietalTypeId);
    const { mutateAsync: updateVarietalType, isLoading: isUpdating } = useUpdateVarietalType();
    const { mutateAsync: deleteVarietalType, isLoading: isDeleting } = useDeleteVarietalType();
    const [showDeletionConfirm, setShowDeletionConfirm] = React.useState(false);

    const typeState = React.useRef(type);

    React.useEffect(() => {
        typeState.current = type;
    }, [type]);

    React.useEffect(() => {
        if (type == null && hasLoaded) {
            replaceNavigation(VarietalTypeNavigation());
        }
    }, [type, hasLoaded, replaceNavigation]);

    const handleSaveInput = React.useCallback((key: string) => async (newValue: any) => {
        if (!Ark.isDefined(typeState.current)) throw new Error('Le cépage n\'a pas été trouvé.');

        try {
            const {
                id,
                label,
                code
            } = typeState.current;

            await updateVarietalType({
                id,
                label,
                code,
                [key]: newValue
            });

            return newValue;
        }
        catch (error) {
            if (!(error instanceof Ark.ServiceError)) {
                errorProvider.notify(new Error('Une erreur est survenue lors de l\'enregistrement.'));
            }
        }
    }, [typeState, updateVarietalType]);

    const handleVarietalTypeDeletion = React.useCallback(async (shouldDelete: boolean) => {
        if (shouldDelete === false) return setShowDeletionConfirm(false);
        if (!Ark.isDefined(typeState.current)) throw new Error('Le cépage n\'a pas été trouvé.');

        await deleteVarietalType(typeState.current.id);

        setShowDeletionConfirm(false);
        navigate(VarietalTypeNavigation());
    }, [typeState, deleteVarietalType, navigate, setShowDeletionConfirm]);

    if (!Ark.isDefined(type)) return null;

    return (
        <>
            <Helmet>
                <title>{t('varietalType.details.pageTitle', { appName: config.appName })}</title>
            </Helmet>
            <Page>
                <Page.Header>
                    <Page.Title>
                        {type?.label}
                    </Page.Title>
                    <Page.HeaderActions>
                        <VarietalTypeDetailsBreadcrumb label={type?.label ?? ''} />
                    </Page.HeaderActions>
                </Page.Header>
                <Page.Content className="row">
                    <Card className="col">
                        <Card.Header>
                            <Card.Title>
                                Informations du cépage
                            </Card.Title>
                        </Card.Header>
                        <Card.Body>
                            {isLoading && (
                                <Loader text="chargement" />
                            )}
                            <Form.Group controlId="varietalType-code" className="col col-6">
                                <Form.Label>Code</Form.Label>
                                <Form.Editable.Input
                                    value={type?.code}
                                    onSave={handleSaveInput('code')}
                                />
                            </Form.Group>
                            <Form.Group controlId="varietalType-label" className="col col-6">
                                <Form.Label>Nom</Form.Label>
                                <Form.Editable.Input
                                    value={type?.label}
                                    onSave={handleSaveInput('label')}
                                />
                            </Form.Group>
                        </Card.Body>
                        <Card.Footer className="row">
                            {Ark.isDefined(type) && (
                                <Card.Infos>
                                    <FooterDateInfo creationDate={type?.creationDate} />
                                </Card.Infos>
                            )}
                            <Card.Actions>
                                <Button
                                    color="danger"
                                    variant="outlined"
                                    startIcon={(<TrashIcon />)}
                                    pending={isDeleting}
                                    disabled={isDeleting || isUpdating}
                                    // can't be deleted if readOnly obsType
                                    onClick={() => setShowDeletionConfirm(true)}
                                >
                                    Supprimer le cépage
                                </Button>
                                {showDeletionConfirm && (
                                    <ConfirmDialog
                                        title={`Supprimer le cépage "${type?.label}"`}
                                        message="Vous êtes sur le point de supprimer ce cépage. Êtes-vous sûr de vouloir continuer ?"
                                        ok="Supprimer"
                                        onClose={handleVarietalTypeDeletion}
                                    />
                                )}
                            </Card.Actions>
                        </Card.Footer>
                    </Card>
                    <Card className="col col-4">
                        <Card.Header>
                            <Card.Title>
                                Utilisation
                            </Card.Title>
                        </Card.Header>
                        <Card.Body className={styles['cardContent']}>
                            <SidePanelList>
                                <SidePanelListItem>
                                    <SidePanelListItemCol className="col-8" text="Nombre de parcelles associées" />
                                    <SidePanelListItemCol className="col-4" text={Ark.isNumber(type?.plotCount) ? type?.plotCount.toString() : '0'} />
                                </SidePanelListItem>
                            </SidePanelList>
                        </Card.Body>
                    </Card>
                </Page.Content>
            </Page>
        </>
    );
};

export default withRouter(VarietalTypeDetailsScreen, 'VARIETALTYPE_DETAILS');